import React, { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { productSaveService } from '../../../services/productSaveService';
import { lastUpdateTimeService } from '../../../services/lastUpdateTimeService';
import { getFetchRequest, PRODUCT_URL } from '../../../store/requests';
import { errorCallback, makeUrl } from '../../../util';
import {
    compositionStatusBadgeClass,
    isCompositionCheck,
    type CompositionCheck,
} from '../../../services/compositionCheck';

const BASIS_OPTIONS = ['raw', 'cooked', 'as_sold', 'prepared'] as const;
const VARIANT_OPTIONS = ['', 'dried', 'fermented', 'pickled', 'salted'] as const;
const FLOUR_KIND_OPTIONS = ['', 'rye', 'wheat_hard', 'wheat_soft', 'oat', 'rice', 'buckwheat', 'corn', 'soy', 'hemp'] as const;
const FLOUR_GRADE_OPTIONS = ['', 'extra', 'first', 'second', 'seyana', 'obdirnaya', 'oboynaya', 'dietary'] as const;

type ProductVariantSummary = {
    productCode: string;
    variantKind: string;
};

type ProductRelationSummary = {
    relatedProductCode: string;
    relationKind: string;
};

type ProductCompositionView = {
    compositionBasis?: string | null;
    variantKind?: string | null;
    flourGrainKind?: string | null;
    flourMillingGrade?: string | null;
    variants?: ProductVariantSummary[];
    relations?: ProductRelationSummary[];
};

type CompositionPatch = {
    compositionBasis?: string;
    variantKind?: string;
    flourGrainKind?: string;
    flourMillingGrade?: string;
};

type Props = {
    productId: string;
};

const readProduct = (): ProductCompositionView => {
    const raw = productSaveService.getProduct() as ProductCompositionView;
    return {
        compositionBasis: raw?.compositionBasis,
        variantKind: raw?.variantKind,
        flourGrainKind: raw?.flourGrainKind,
        flourMillingGrade: raw?.flourMillingGrade,
        variants: Array.isArray(raw?.variants) ? raw.variants : [],
        relations: Array.isArray(raw?.relations) ? raw.relations : [],
    };
};

const ProductCompositionFields = ({ productId }: Props) => {
    const { i18n } = useTranslation();
    const [product, setProduct] = useState<ProductCompositionView>(readProduct);
    const [compositionCheck, setCompositionCheck] = useState<CompositionCheck | null>(null);

    useEffect(() => {
        const refresh = () => setProduct(readProduct());
        refresh();
        productSaveService.registerItemUpdateObserver(refresh);
        return () => productSaveService.unRegisterItemUpdateObserver(refresh);
    }, [productId]);

    useEffect(() => {
        if (!productId) {
            return undefined;
        }
        let cancelled = false;
        const loadCheck = () => {
            getFetchRequest(
                makeUrl([PRODUCT_URL, productId, '/composition-check/']),
                (payload: unknown) => {
                    if (!cancelled) {
                        setCompositionCheck(isCompositionCheck(payload) ? payload : null);
                    }
                },
                errorCallback,
            );
        };
        loadCheck();
        lastUpdateTimeService.registerUpdateEventObserver(loadCheck);
        return () => {
            cancelled = true;
            lastUpdateTimeService.unRegisterUpdateEventObserver(loadCheck);
        };
    }, [productId]);

    const patch = (fields: CompositionPatch) => productSaveService.updateComposition(fields);

    const onSelect = (key: keyof CompositionPatch) => (e: ChangeEvent<HTMLSelectElement>) => {
        patch({ [key]: e.target.value });
    };

    const variants = product.variants ?? [];
    const relations = product.relations ?? [];

    return (
        <div className="product__form">
            <label className="block__text product__form-label">
                <span className="product__form-label_text">
                    {i18n.t('app.page.product.compositionBasis')}
                </span>
                <select
                    className="product__form-input"
                    value={product.compositionBasis || 'raw'}
                    onChange={onSelect('compositionBasis')}
                >
                    {BASIS_OPTIONS.map((value) => (
                        <option key={value} value={value}>{value}</option>
                    ))}
                </select>
            </label>
            <label className="block__text product__form-label">
                <span className="product__form-label_text">
                    {i18n.t('app.page.product.variantKind')}
                </span>
                <select
                    className="product__form-input"
                    value={product.variantKind || ''}
                    onChange={onSelect('variantKind')}
                >
                    {VARIANT_OPTIONS.map((value) => (
                        <option key={value || 'none'} value={value}>{value || '—'}</option>
                    ))}
                </select>
            </label>
            <label className="block__text product__form-label">
                <span className="product__form-label_text">
                    {i18n.t('app.page.product.flourGrainKind')}
                </span>
                <select
                    className="product__form-input"
                    value={product.flourGrainKind || ''}
                    onChange={onSelect('flourGrainKind')}
                >
                    {FLOUR_KIND_OPTIONS.map((value) => (
                        <option key={value || 'none'} value={value}>{value || '—'}</option>
                    ))}
                </select>
            </label>
            <label className="block__text product__form-label">
                <span className="product__form-label_text">
                    {i18n.t('app.page.product.flourMillingGrade')}
                </span>
                <select
                    className="product__form-input"
                    value={product.flourMillingGrade || ''}
                    onChange={onSelect('flourMillingGrade')}
                >
                    {FLOUR_GRADE_OPTIONS.map((value) => (
                        <option key={value || 'none'} value={value}>{value || '—'}</option>
                    ))}
                </select>
            </label>
            {variants.length > 0 && (
                <p className="block__text">
                    {i18n.t('app.page.product.variants')}:{' '}
                    {variants.map((it) => `${it.productCode} (${it.variantKind})`).join(', ')}
                </p>
            )}
            {relations.length > 0 && (
                <p className="block__text">
                    {i18n.t('app.page.product.related')}:{' '}
                    {relations.map((it) => `${it.relatedProductCode} (${it.relationKind})`).join(', ')}
                </p>
            )}
            <div className="mt-12">
                <p className="block__text">
                    {i18n.t('app.page.product.compositionCheck.title')}
                    {compositionCheck ? (
                        <span className={`${compositionStatusBadgeClass(compositionCheck.status)} ml-8`}>
                            {i18n.t(`app.page.product.compositionCheck.status.${compositionCheck.status}`)}
                        </span>
                    ) : (
                        <span className="block__bullet ml-8">
                            {i18n.t('app.page.product.compositionCheck.loading')}
                        </span>
                    )}
                </p>
                {compositionCheck?.referenceBookCode ? (
                    <p className="block__bullet">
                        {i18n.t('app.page.product.compositionCheck.provenance')}: {compositionCheck.referenceBookCode}
                    </p>
                ) : null}
                <p className="block__bullet">
                    {i18n.t('app.page.product.compositionCheck.hint')}
                </p>
                {(compositionCheck?.branches || []).map((branch) => (
                    <p key={branch.code} className="block__bullet">
                        {i18n.t(`app.page.product.compositionCheck.branch.${branch.code}`)}
                        {': '}
                        {i18n.t(`app.page.product.compositionCheck.status.${branch.status}`)}
                    </p>
                ))}
                {(compositionCheck?.warnings || []).map((warning) => (
                    <p key={`${warning.rule}-${warning.parent}`} className="block__bullet alert">
                        {i18n.t(`app.page.product.compositionCheck.rule.${warning.rule}`, {
                            sum: warning.sumGPer100g.toFixed(2),
                            parent: warning.parentGPer100g.toFixed(2),
                            tolerance: warning.toleranceGPer100g.toFixed(2),
                        })}
                    </p>
                ))}
            </div>
        </div>
    );
};

export default ProductCompositionFields;
